// Convert decimal days to days and hours, rounding minutes to the nearest hour
export const formatDaysHours = (days) => {
  if (!days || isNaN(days)) return '';
  const totalMinutes = days * 24 * 60;
  const daysInt = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.round((totalMinutes % (24 * 60)) / 60); // Round minutes to nearest hour
  const parts = [];
  if (daysInt > 0) parts.push(`${daysInt}d`);
  if (hours > 0 || daysInt === 0) parts.push(`${hours}h`);
  return parts.length > 0 ? parts.join(', ') : '0h';
};

// Convert decimal days to total minutes for sorting
export const getTotalMinutes = (days) => {
  if (!days || isNaN(days)) return 0;
  return days * 24 * 60;
};

export const departmentRealAlias = (d) => {
  const raw = Array.isArray(d?.name_alias) ? d.name_alias[0] : d?.name_alias;
  const alias = (raw || '').toString().trim();
  return alias ? alias.replace(/\b\w/g, c => c.toUpperCase()) : null;
};

export const departmentAliasLabel = d => departmentRealAlias(d) || (d?.display_name || d?.name || 'Unknown');

export const ALIAS_ORDER = [
  'in transit',
  'ready for service',
  'inspection',
  'awaiting approvals',
  'parts hold',
  'on hold',
  'in service',
  'offsite',
  'vendor/body shop',
  'in detail',
  'needs photos',
  'frontline ready',
  'wholesale',
  'needs attention',
];

// Get the last 6 months in step_YYYY_MM format (for department tables)
export const getLastSixMonthsReportTypes = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    months.push(`step_${year}_${month}`);
  }
  return months;
};

// Get the last 6 months in work_flow_YYYY_MM format (for workflow tables)
export const getLastSixMonthsWorkFlowReportTypes = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    months.push(`work_flow_${year}_${month}`);
  }
  return months;
};

// Compute average time based on dealershipReportHours
export const computeAverageTime = (item, dealershipReportHours) => {
  if (!item) return null;
  const rawtime_24_5 = item.rawtime_24_5 || 0;
  const rawtime_24_6 = item.rawtime_24_6 || 0;
  const rawtime_24_7 = item.rawtime_24_7 || 0;
  if (dealershipReportHours === 'age-24_7') {
    return (rawtime_24_7 / 24).toFixed(6);
  } else if (dealershipReportHours === 'age-24_6') {
    return (rawtime_24_6 / 24).toFixed(6);
  } else {
    return (rawtime_24_5 / 24).toFixed(6);
  }
};

// Get department average for a specific time range (for department tables)
export const getDepartmentAverageForRange = (department_id, time_range, reportDepartmentData, dealershipReportHours, propsData = null, dealership) => {
  if (!department_id || !time_range || !dealership) {
    return null;
  }
  const entry = reportDepartmentData?.find(item => 
    item?.department_id === department_id && 
    item?.report_type?.trim().toLowerCase() === `step-${time_range}`.toLowerCase() &&
    item?.dealership === dealership
  );
  if (entry) {
    const average = computeAverageTime(entry, dealershipReportHours);
    return average;
  }
  if (propsData && propsData.xAxis && propsData.yAxis && propsData.departmentId) {
    const index = propsData.departmentId.findIndex(id => id === department_id);
    if (index !== -1) {
      const dealerships = Object.keys(propsData.yAxis);
      const averages = dealerships
        .filter(d => propsData.yAxis[d]?.[index])
        .map(d => Number(propsData.yAxis[d][index]))
        .filter(avg => !isNaN(avg));
      if (averages.length > 0) {
        const average = (averages.reduce((sum, avg) => sum + avg, 0)).toFixed(6);
        return average;
      }
    }
  }
  return null;
};

// Get workflow average for a specific time range (for workflow tables)
export const getWorkFlowAverageForRange = (work_flow_id, time_range, reportDepartmentData, dealershipReportHours, propsData = null, dealership) => {
  if (!work_flow_id || !time_range || !dealership) {
    return null;
  }
  const entry = reportDepartmentData?.find(item => 
    item?.work_flow_id === work_flow_id && 
    item?.report_type?.trim().toLowerCase() === `work_flow_${time_range}`.toLowerCase() &&
    item?.dealership === dealership
  );
  // console.log(`getWorkFlowAverageForRange: workflow=${work_flow_id}, range=${time_range}, dealership=${dealership}`, { entry }); // Commented out: not needed for modal debugging
  if (entry) {
    const average = computeAverageTime(entry, dealershipReportHours);
    return average;
  }
  if (propsData && propsData.xAxis && propsData.yAxis && propsData.departmentId) {
    const index = propsData.departmentId.findIndex(id => id === work_flow_id);
    if (index !== -1) {
      const dealerships = Object.keys(propsData.yAxis);
      const averages = dealerships
        .filter(d => propsData.yAxis[d]?.[index])
        .map(d => Number(propsData.yAxis[d][index]))
        .filter(avg => !isNaN(avg));
      if (averages.length > 0) {
        const average = (averages.reduce((sum, avg) => sum + avg, 0) / averages.length).toFixed(6);
        // console.log(`getWorkFlowAverageForRange fallback: workflow=${work_flow_id}, range=${time_range}, average=${average}`); // Commented out: not needed for modal debugging
        return average;
      }
    }
  }
  return null;
};

// Get group average for a specific time range (weighted by department_id, for department tables)
export const getGroupAverageForRange = (time_range, reportDepartmentData, dealershipReportHours, visibleDealers = null, department_id) => {
  if (!time_range || !reportDepartmentData) {
    return null;
  }
  const uniqueDealerships = visibleDealers
    ? getUniqueDealerships(reportDepartmentData).filter(dealer => visibleDealers[dealer])
    : getUniqueDealerships(reportDepartmentData);
  let totalWeightedTime = 0;
  let totalVehicles = 0;
  uniqueDealerships.forEach(dealership => {
    const entry = reportDepartmentData.find(item => 
      item?.dealership === dealership && 
      item?.report_type?.trim().toLowerCase() === `step-${time_range}`.toLowerCase() && 
      (!department_id || item?.department_id === department_id)
    );
    if (entry && entry.vehicle_count) {
      const average = computeAverageTime(entry, dealershipReportHours);
      if (average !== null && !isNaN(average)) {
        totalWeightedTime += Number(average) * Number(entry.vehicle_count);
        totalVehicles += Number(entry.vehicle_count);
      }
    }
  });
  if (totalVehicles === 0) {
    return null;
  }
  const average = (totalWeightedTime / totalVehicles).toFixed(6);
  return average;
};

// Get group average for workflow in a specific time range (weighted by work_flow_id, for workflow tables)
export const getGroupWorkFlowAverageForRange = (time_range, reportDepartmentData, dealershipReportHours, visibleDealers = null, work_flow_id) => {
  if (!time_range || !reportDepartmentData) {
    return null;
  }
  const uniqueDealerships = visibleDealers
    ? getUniqueDealerships(reportDepartmentData).filter(dealer => visibleDealers[dealer])
    : getUniqueDealerships(reportDepartmentData);
  let totalWeightedTime = 0;
  let totalVehicles = 0;
  uniqueDealerships.forEach(dealership => {
    const entry = reportDepartmentData.find(item => 
      item.dealership && dealership && item.dealership.toLowerCase() === dealership.toLowerCase() && 
      item?.report_type?.trim().toLowerCase() === `work_flow_${time_range}`.toLowerCase() && 
      (!work_flow_id || item?.work_flow_id === work_flow_id)
    );
    // console.log(`getGroupWorkFlowAverageForRange: dealership=${dealership}, range=${time_range}, workflow=${work_flow_id}`, { entry }); // Commented out: not needed for modal debugging
    if (entry && entry.vehicle_count) {
      const average = computeAverageTime(entry, dealershipReportHours);
      if (average !== null && !isNaN(average)) {
        totalWeightedTime += Number(average) * Number(entry.vehicle_count);
        totalVehicles += Number(entry.vehicle_count);
      }
    }
  });
  if (totalVehicles === 0) {
    // console.log(`getGroupWorkFlowAverageForRange: No vehicles for range=${time_range}, workflow=${work_flow_id}`, { uniqueDealerships, totalWeightedTime, totalVehicles }); // Commented out: not needed for modal debugging
    return null;
  }
  const average = (totalWeightedTime / totalVehicles).toFixed(6);
  // console.log(`getGroupWorkFlowAverageForRange: range=${time_range}, workflow=${work_flow_id}, average=${average}`, { totalWeightedTime, totalVehicles, uniqueDealerships }); // Commented out: not needed for modal debugging
  return average;
};

// Get unique dealerships from report_department_data
export const getUniqueDealerships = (data) => {
  return [...new Set(data?.map(item => item?.dealership).filter(Boolean) || [])];
};

// Get dealership average for a specific time range (for department tables)
export const getDealershipAverageForRange = (dealership, time_range, reportDepartmentData, dealershipReportHours, department_id = null) => {
  if (!dealership || !time_range || !reportDepartmentData) {
    return null;
  }
  const entry = reportDepartmentData.find(item => 
    item?.dealership === dealership && 
    item?.report_type?.trim().toLowerCase() === `step-${time_range}`.toLowerCase() && 
    (!department_id || item?.department_id === department_id)
  );
  if (!entry) {
    return null;
  }
  const average = computeAverageTime(entry, dealershipReportHours);
  return average;
};

// Get dealership average for a specific time range (for workflow tables)
export const getDealershipWorkFlowAverageForRange = (dealership, time_range, reportDepartmentData, dealershipReportHours, work_flow_id = null) => {
  if (!dealership || !time_range || !reportDepartmentData) {
    return null;
  }
  const entry = reportDepartmentData.find(item => 
    item.dealership && dealership && item.dealership.toLowerCase() === dealership.toLowerCase() && 
    item?.report_type?.trim().toLowerCase() === `work_flow_${time_range}`.toLowerCase() && 
    (!work_flow_id || item?.work_flow_id === work_flow_id)
  );
  // console.log(`getDealershipWorkFlowAverageForRange: dealership=${dealership}, range=${time_range}, workflow=${work_flow_id}`, { entry }); // Commented out: not needed for modal debugging
  if (!entry) {
    return null;
  }
  const average = computeAverageTime(entry, dealershipReportHours);
  return average;
};

// Get vehicle count for a dealership in a specific time range (for department tables)
export const getDealershipVehicleCount = (dealership, time_range, reportDepartmentData, department_id = null) => {
  if (!dealership || !time_range || !reportDepartmentData) {
    return 0;
  }
  const entry = reportDepartmentData.find(item => 
    item?.dealership === dealership && 
    item?.report_type?.trim().toLowerCase() === `step-${time_range}`.toLowerCase() && 
    (!department_id || item?.department_id === department_id)
  );
  if (!entry) {
    return 0;
  }
  const totalCount = entry ? Number(entry.vehicle_count) : 0;
  return totalCount;
};

// Get vehicle count for a dealership in a specific time range (for workflow tables)
export const getDealershipWorkFlowVehicleCount = (dealership, time_range, reportDepartmentData, work_flow_id = null) => {
  if (!dealership || !time_range || !reportDepartmentData) {
    return 0;
  }
  const entry = reportDepartmentData.find(item => 
    item.dealership && dealership && item.dealership.toLowerCase() === dealership.toLowerCase() && 
    item?.report_type?.trim().toLowerCase() === `work_flow_${time_range}`.toLowerCase() && 
    (!work_flow_id || item?.work_flow_id === work_flow_id)
  );
  // console.log(`getDealershipWorkFlowVehicleCount: dealership=${dealership}, range=${time_range}, workflow=${work_flow_id}`, { entry }); // Commented out: not needed for modal debugging
  if (!entry) {
    return 0;
  }
  const totalCount = entry ? Number(entry.vehicle_count) : 0;
  return totalCount;
};

// Group report_vehicles by dealership and completed_departments
export const groupVehiclesByDepartments = (reportVehicles, departments, work_flow_id, dealership = null, dealershipReportHours = 'age-24_5') => {
  if (!reportVehicles || !Array.isArray(reportVehicles) || !departments) {
    // console.warn('groupVehiclesByDepartments: Invalid inputs:', { reportVehicles, departments, work_flow_id, dealershipReportHours });
    return [];
  }

  // Filter vehicles by optional dealership only
  const filteredVehicles = reportVehicles.filter(vehicle => 
    (!dealership || vehicle.dealership === dealership) && vehicle.dealership && vehicle.completed_departments
  );
  // console.log(`groupVehiclesByDepartments: Filtered vehicles for workflow report, dealership ${dealership || 'all'}`, { filteredVehicles });

  // Group by dealership and department_id
  const departmentMap = filteredVehicles.reduce((acc, vehicle) => {
    const dealer = vehicle.dealership;
    if (!acc[dealer]) {
      acc[dealer] = {};
    }
    if (vehicle.completed_departments && Array.isArray(vehicle.completed_departments)) {
      vehicle.completed_departments.forEach(dept => {
        const deptId = dept.department_id;
        if (!acc[dealer][deptId]) {
          const deptInfo = departments.find(d => parseInt(d.id) === parseInt(deptId));
          acc[dealer][deptId] = {
            dealership: dealer,
            department_id: deptId,
            name: deptInfo ? (deptInfo.display_name || deptInfo.name || 'Unknown') : 'Unknown',
            vehicleCount: 0,
            totalDuration: 0,
          };
        }
        acc[dealer][deptId].vehicleCount += 1;
        // Calculate duration based on dealership_report_hours
        let durationSeconds = 0;
        if (dealershipReportHours === 'age-24_7') {
          durationSeconds = Number(dept.recon_total_24_7) || 0;
        } else if (dealershipReportHours === 'age-24_6') {
          durationSeconds = Number(dept.recon_total_24_6) || 0;
        } else {
          durationSeconds = Number(dept.recon_total_24_5) || 0;
        }
        // Convert seconds to days
        const durationDays = durationSeconds / (24 * 60 * 60);
        acc[dealer][deptId].totalDuration += durationDays || 0;
      });
    }
    return acc;
  }, {});

  // Convert to array and compute averages
  const result = Object.values(departmentMap).flatMap(dealerMap => 
    Object.values(dealerMap).map(dept => ({
      dealership: dept.dealership,
      department_id: dept.department_id,
      name: dept.name,
      vehicle_count: dept.vehicleCount,
      average_time: dept.vehicleCount > 0 ? (dept.totalDuration / dept.vehicleCount).toFixed(6) : 0,
    }))
  );

  // console.log(`groupVehiclesByDepartments: Result for workflow report, dealership ${dealership || 'all'}`, result);
  return result;
};